import Slider from "react-slick"
import { Icon, Text, Box, Tag, Btn } from '../common'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { userInfoData } from '../../hooks/useAuth'
import { UrlsAddressess } from '../../hooks/config'
import { getFormatDate, getFormatTime } from '../../utils/date'
import { useFeeds, Feed, Notification } from './UseFeeds'
import { ReactElement, useState, useEffect, createRef, useRef, useMemo } from 'react'

export const NotificationsPanel = ({
  onSettingClick,
  userInfo,
  userToken,
  closeDropdown,
  urls
}: {
  onSettingClick: () => void
  closeDropdown: () => void
  userInfo: userInfoData
  userToken: string
  urls: UrlsAddressess
}) => {
  const {
    selectFeed,
    fetchNextPage,
    selectIsRead,
    unReadCountTab,
    feeds,
    hasNextPage,
    notifications,
    isLoading,
    isRead,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    markAsUnRead,
    selectedFeed
  } = useFeeds({ userToken: userToken, urls:urls })

  return (
    <div>
      <Header
        markAllAsRead={markAllAsRead}
        unReadCountTab={unReadCountTab}
        onSettingClick={onSettingClick}
        userInfo={userInfo}
        isRead={isRead}
        selectIsRead={selectIsRead}
        deleteAllNotifications={deleteAllNotifications}
        feeds={feeds}
        selectFeed={selectFeed}
        selectedFeed={selectedFeed}
        closeDropdown={closeDropdown}
      />
      <Body
        notifications={notifications}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        markAsRead={markAsRead}
        deleteNotification={deleteNotification}
        isRead={isRead}
        userInfo={userInfo}
        markAsUnRead={markAsUnRead}
        selectedFeed={selectedFeed}
      />
    </div>
  );
}

const Header = ({
  markAllAsRead,
  onSettingClick,
  unReadCountTab,
  userInfo,
  isRead,
  selectIsRead,
  deleteAllNotifications,
  feeds,
  selectFeed,
  selectedFeed,
  closeDropdown
}: {
  markAllAsRead: () => void,
  unReadCountTab: string,
  onSettingClick: () => void,
  userInfo: userInfoData,
  isRead: boolean,
  feeds: Feed[],
  selectIsRead: (read: boolean) => void,
  deleteAllNotifications: () => void,
  selectFeed: (feed: Feed) => void,
  selectedFeed: Feed,
  closeDropdown: () => void,
}) => {
  const [hasArrows, setHasArrows] = useState<boolean>(false)
  return (
    <div className='notifications-header'>
      <div className="notifications-header-top">
        <Text weight="medium" size="xl">
          {userInfo.translations.notifications_inbox_title}
        </Text>
        <div className='notification-hader-right'>
          <Icon
            onClick={onSettingClick}
            title="Ajustes"
            name="icon-settings-2-outline"
            style={{ cursor:'pointer'}}
          />
          <Icon 
            style={{ cursor:'pointer' }}
            size={28}
            onClick={closeDropdown}
            name="icon-close"
          />
        </div>
      </div>
      <div className="notifications-header-tags">
        {feeds.length && (
          <div className='slider-container-tag'>
            <Slider {...{
              appendDots:(dots: ReactElement[]) => {
                setHasArrows((dots?.length) ? true : false)
                return <div />
              },
              dots: true,
              infinite: false,
              autoplay: false,
              speed: 500,
              slidesToScroll: 1,
              slidesToShow: 4,
              variableWidth: true,
              prevArrow: <div />,
              nextArrow: (hasArrows)
                ? <div><Icon className="bg-surface" size="25" name="icon-arrow-down"/></div>
                : <div />
            }}>
              <Tag
                key={selectedFeed.identifier}
                color="primary"
                className={`tag-${selectedFeed.name} text-white`}
                onClick={() => selectFeed(selectedFeed)}
              >
                {selectedFeed.name}
              </Tag>
              {feeds.filter((f) => f.identifier !== selectedFeed.identifier).map((feed) => (
                <Tag
                  key={feed.identifier}
                  className={`slider-tag tag-${feed.name}`}
                  color="surface-2"
                  onClick={() => selectFeed(feed)}
                >
                  {feed.name}
                </Tag>
              ))}
            </Slider>
          </div>
        )}
      </div>
      <div className="notifications-header-bottom">
        <Btn
          onClick={() => selectIsRead(false)}
          size="md"
          weight="medium"
          variant="tonal"
          color="base"
          className={[
            'unread-tab rounded-0',
            { 'border-0 border-bottom-2 border-primary active': !isRead }
          ]}
        >
          {userInfo.translations.notifications_inbox_tab_unread}
          {unReadCountTab && (
            <Text
              className="notifications-unread rounded-pill inline-flex align-center px-2 py-1 ml-2"
              bgColor="primary"
              color="white"
              weight="medium"
              size="xs"
            >
              {unReadCountTab}
            </Text>
          )}
        </Btn>
        <Btn
          onClick={() => selectIsRead(true)} 
          size="md" 
          weight="medium"
          variant="tonal"
          color="base"
          className={[
            'read-tab rounded-0',
            { 'border-0 border-bottom-2 border-primary active': isRead }
          ]}
        >
          {userInfo.translations.notifications_inbox_tab_read}
        </Btn>
        {isRead && (
          <Btn
            size="md"
            weight="medium"
            variant="tonal"
            color="base"
            className="ml-auto"
            onClick={deleteAllNotifications}
          > 
            {userInfo.translations.notifications_set_as_remove}
            <Icon 
              className="ml-3"
              name="icon-delete"
              size={20}
            />
          </Btn>
        )}
        {!isRead && (
          <Btn
            size="md"
            weight="medium"
            variant="tonal"
            color="base"
            className="ml-auto"
            onClick={markAllAsRead}
          >
            {userInfo.translations.notifications_set_as_read}
            <Icon
              className="ml-3"
              name="icon-email-open"
              size={20}
            />
          </Btn>
        )}
      </div>
    </div>
  )
}

const Body = ( {
  notifications,
  isLoading,
  hasNextPage,
  isRead,
  userInfo,
  fetchNextPage,
  markAsRead,
  deleteNotification,
  markAsUnRead,
  selectedFeed
}: {
  notifications: Notification[] | undefined,
  isLoading: boolean,
  hasNextPage: boolean | undefined
  isRead: boolean
  userInfo: userInfoData
  fetchNextPage: () => void
  markAsRead: (notification: Notification) => void
  markAsUnRead: (notification: Notification) => void
  deleteNotification: (notification: Notification) => void
  selectedFeed: Feed
} ) => {
  const [sentryRef] = useInfiniteScroll({
    loading: isLoading ,
    hasNextPage: hasNextPage || false,
    onLoadMore: fetchNextPage,
    rootMargin: '0px 0px 20px 0px',
  });
  const bodyRef = createRef<HTMLDivElement>()
  const [classes, setClasses] = useState<string>('')
  // const [addTrans, setAddTrans] = useState<boolean>(false)

  useEffect(() => {
    bodyRef.current?.scrollTo(0, 0)
  }, [isRead, selectedFeed])

  useEffect(() => {
    setClasses('notifications-body-transitions')
    setTimeout(() => {
      setClasses('')
    }, 1200)
  }, [notifications])

  return (
    <div
      ref={bodyRef}
      className={`notifications-body ${classes}`}
    >
    {Array.isArray(notifications) && notifications?.length > 0 ? (
      <div className="notifications-body-inner">
        {notifications?.map((notification: Notification) => (
          <NotificationItem
            key={notification._id}
            notification={notification}
            markAsRead={markAsRead}
            deleteNotification={deleteNotification}
            userInfo={userInfo}
            markAsUnRead={markAsUnRead}
          />
        ))}
        <div className='sentry' style={{ height: '1px' }} ref={sentryRef} />
        {isLoading && (
          <div className='notification-is-loading'>
            {userInfo.translations.notifications_loading}
          </div>
        )}
      </div>
      ) : (
      <div className="notification-empty-message">
        <EmptyList isRead={isRead} userInfo={userInfo} />
      </div>
      )}
  </div>
  )
}

const NotificationItem = ({
  notification,
  markAsRead,
  deleteNotification,
  userInfo,
  markAsUnRead,
}: {
  notification: Notification,
  markAsRead: (notification: Notification) => void
  markAsUnRead: (notification: Notification) => void
  deleteNotification: (notification: Notification) => void
  userInfo: userInfoData
}) => {
  const payloadData = useMemo(() => notification.payload[userInfo.language] || {}, [userInfo.language, notification.payload])
  const title = useMemo(() => payloadData.title || '', [payloadData])
  const body = useMemo(() => payloadData.body || '', [payloadData])

  // Get format date
  const formatDateTime = useMemo(() => getFormatDate(notification.createdAt), [notification.createdAt])
  const fmtTime = getFormatTime(notification.createdAt, {
    lessOneHour: userInfo.translations.notifications_less_one_hour,
    someHours: userInfo.translations.notifications_less_hours,
    someDay: userInfo.translations.notifications_less_one_day,
    someDays: userInfo.translations.notifications_less_days
  })

  const [isReadChange, setIsReadChange] = useState<boolean>(false)
  const firstUpdateRead = useRef(false)

  useEffect(() => {
    if (firstUpdateRead.current) {
      setIsReadChange(true)
    }
    firstUpdateRead.current = true
  }, [notification.read])

  const [isDeletingChange, setIsDeletingChange] = useState<boolean>(false)
  const firstUpdateDeleting = useRef(false)

  useEffect(() => {
    if (firstUpdateDeleting.current) {
      setIsDeletingChange(true)
    }
    firstUpdateDeleting.current = true
  }, [notification.deleting])

  return (
      <Box
        className={[
          'notifications-notification',
          notification.read ? 'is-read' : 'not-read',
          isReadChange ? 'notification-exit-transition' : 'not-change',
          isDeletingChange ? 'notification-exit-transition ' : 'not-change'
        ]}
      >
        <div className="line" />
        <div className="avatar" style={{ backgroundImage: `url("${notification.actor?.data}")`}} />
        <div className='content'>
          <div className="content-top">
            <div className="content-top-left">
              <Text className="title" weight="medium" tag="p" dangerouslySetInnerHTML={{ __html: title }} />
              <Text className="body text-lighten-2" color="base" tag="p" dangerouslySetInnerHTML={{ __html: body }} />
            </div>
            <div className="content-top-right">
              {!notification.read && (
                <Icon
                  onClick={() => markAsRead(notification)}
                  title={userInfo.translations.notifications_set_as_read_tip}
                  className="close icon-email-open"
                  name="icon-email-open"
                  size={20}
                  color="white"
                  style={{ cursor: 'pointer'}}
                />
              )}
              {notification.read && (
                <div>
                  <Icon
                    onClick={() => markAsUnRead(notification)}
                    title={userInfo.translations.notifications_set_as_unread_tip}
                    className="close icon-email-bullet"
                    name="icon-email-bullet"
                    size="xl"
                    style={{
                      cursor: 'pointer',
                      marginRight: '5px',
                    }}
                  />
                  <Icon
                    onClick={() => deleteNotification(notification)}
                    title={userInfo.translations.notifications_set_as_remove_tip}
                    className="close icon-delete"
                    name="icon-delete"
                    size="xl"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="content-bottom">
            <div className="content-bottom-left">
              {notification.cta?.action?.buttons && (
                <div className="button-box">
                  {notification.cta?.action?.buttons.map((button) => (
                    <Btn
                      key={button.type}
                      color={button.type}
                      underline={false}
                      weight="bold"
                      className={["action-button", { 'text-white': button.type === 'primary' }]}
                      onClick={() => {
                        window.open(payloadData[button.type], '_blank')
                      }}
                    >
                      {button.content}
                    </Btn>  
                  ))}
                </div>
              )}
            </div>
            <div className="content-bottom-right">
              <Text
                className="received-time"
                title={formatDateTime}
                size="xs"
              >
                {fmtTime || formatDateTime}
              </Text>
            </div>
          </div>
        </div>
      </Box>
  )
}

const EmptyList = ({ isRead, userInfo }: { isRead: boolean, userInfo: userInfoData}) => {
  return (
    <div className="empty-list">
      <div className="empty-list-icon">
        <Icon name="icon-bell" size="82"/>
      </div>
      <Text tag="div" className="empty-list-text-title" size="xl" weight="bold"> 
        {
          isRead
            ? userInfo.translations.notifications_empty_unread_notifications
            : userInfo.translations.notifications_empty_notifications
        }
      </Text>
      <Text tag="div" className="empty-list-text-description">
        {userInfo.translations.notifications_empty_message }
      </Text>
    </div>
  )
}